import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'react-i18next';
import { isArray, isEmpty, isNil, isString, sortBy } from 'lodash';
// Import Types
import type { MenuItemDropDownConfig } from './types';
// Import Helpers
import { Helpers } from 'services';

const { getTranslatableErrorText } = Helpers;

type SetNestedMenuItemsData = Dispatch<SetStateAction<Record<string | number, unknown[]>>>;

const getNestedMenuItemsData = (menuItemId: string | number, setNestedMenuItemsData: SetNestedMenuItemsData) => {
    return (data: unknown[]) => {
        if (isArray(data)) {
            setNestedMenuItemsData((prev) => {
                return {
                    ...prev,
                    [menuItemId]: data || [],
                };
            });
        }
    };
};

export const getColumns = ({ t, config, displayName, setNestedMenuItemsData }) => {
    if (isNil(config)) {
        return [];
    }

    const filteredConfig = config.option.groups.flat().filter((item) => !item.isId);

    const columns = filteredConfig.map((item) => {
        const returnValue: MenuItemDropDownConfig = {
            key: item.name,
            filterable: item.filterable,
            label: t(
                `${config.option.keyGroupNameColumn?.name === item.name ? displayName : item.name}Label${
                    !isEmpty(item.idColumn) && item.idColumn.filterable ? 'WithId' : ''
                }`,
            ),
            idColumn: item.idColumn,
            orderKey: item.orderKey,
        };

        if (!isNil(item.menuItemId)) {
            returnValue.menuItemId = item.menuItemId;
            returnValue.setMenuItemOptions = getNestedMenuItemsData(item.menuItemId, setNestedMenuItemsData);
        }

        return returnValue;
    });

    return sortBy(columns, 'orderKey');
};

export const getListData = (options) => {
    const {
        data,
        searchData,
        config,
        columns: listColumns,
        supportedValues,
        nestedMenuItemsData,
        showSelected,
        value,
        dropdownExternalOptions,
    } = options;

    const isSearched = !isEmpty(searchData) && data.length !== searchData.length;

    const supported = supportedValues?.map((item) => item?.toString());

    const expectedData = showSelected ? data : isSearched ? searchData : [...(dropdownExternalOptions || []), ...data];

    return expectedData
        .map((item) => {
            const result = listColumns.reduce(
                (acc, column) => {
                    if (!isNil(column.menuItemId)) {
                        const menuItems = nestedMenuItemsData[column.menuItemId] ?? [];

                        const foundMenuItem = menuItems.find((option) => +option.Id === +item[column.key]);

                        acc[column.key] = foundMenuItem?.Name || item[column.key];
                    } else {
                        acc[column.key] = item[column.key];
                    }
                    if (!isNil(column.idColumn) && column.idColumn.filterable) {
                        acc[column.key] = `${acc[column.key]} (${item[column.idColumn.name]})`;
                    }
                    return acc;
                },
                { [config?.keyColumnName]: item[config?.keyColumnName]?.toString() },
            );
            // console.log('result', result);
            return result;
        })
        .filter((item) => {
            return (
                (!showSelected || value.includes(item[config?.keyColumnName].toString())) &&
                (!isNil(supported) ? supported.includes(item[config?.keyColumnName].toString()) : true)
            );
        });
};

export const mapToValue = (value?: unknown[] | unknown | null) => {
    if (isNil(value)) return [];

    return isArray(value) ? value : [value];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getDefaultTranslatableErrorText = (t: TFunction, error?: any) => {
    return getTranslatableErrorText(t, [{ errorMessage: isString(error) ? error : error?.label, errorParams: error }]);
};

const getValue = (value?: string | number) => {
    return isNil(value) ? null : `${value}`;
};

export const getValues = (value?: string | string[], isMultiselect = false) => {
    if (isNil(value)) {
        return isMultiselect ? [] : null;
    }
    if (isArray(value)) {
        return isMultiselect ? value.map(getValue) : null;
    }
    return getValue(value);
};
